"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { useAuthErrorMessages } from "@saas/auth/hooks/errors-messages";
import { sessionQueryKey } from "@saas/auth/lib/api";
import { OrganizationInvitationAlert } from "@saas/organizations/components/OrganizationInvitationAlert";
import { useRouter } from "@shared/hooks/router";
import { apiClient } from "@shared/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
} from "@ui/components/form";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import {
	AlertTriangleIcon,
	ArrowRightIcon,
	EyeIcon,
	EyeOffIcon,
	KeyIcon,
	MailboxIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { withQuery } from "ufo";
import { z } from "zod";
import {
	type OAuthProvider,
	oAuthProviders,
} from "../constants/oauth-providers";
import { useSession } from "../hooks/use-session";
import { LoginModeSwitch } from "./LoginModeSwitch";
import { SocialSigninButton } from "./SocialSigninButton";

const formSchema = z.union([
	z.object({
		mode: z.literal("magic-link"),
		email: z.string().email(),
	}),
	z.object({
		mode: z.literal("password"),
		email: z.string().email(),
		password: z.string().min(1),
	}),
]);

type FormValues = z.infer<typeof formSchema>;

export function LoginForm() {
	const t = useTranslations();
	const { getAuthErrorMessage } = useAuthErrorMessages();
	const router = useRouter();
	const queryClient = useQueryClient();
	const searchParams = useSearchParams();
	const { user, loaded: sessionLoaded } = useSession();

	const [showPassword, setShowPassword] = useState(false);
	// Email-first: an address is collected before any credential, so an
	// organization whose domain is claimed by an identity provider can be sent
	// there instead of being shown a password field it has no password for.
	const [step, setStep] = useState<"email" | "credentials" | "two-factor">(
		"email",
	);
	const [isResolvingMode, setIsResolvingMode] = useState(false);
	const [isRedirectingToSso, setIsRedirectingToSso] = useState(false);
	// The TOTP challenge. better-auth holds the pending sign-in in a signed
	// `two_factor` cookie, so nothing about it needs to be carried in the form.
	const [twoFactorCode, setTwoFactorCode] = useState("");
	const [useBackupCode, setUseBackupCode] = useState(false);
	const [trustDevice, setTrustDevice] = useState(false);
	const [isVerifyingTwoFactor, setIsVerifyingTwoFactor] = useState(false);
	const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
	const invitationId = searchParams.get("invitationId");
	const email = searchParams.get("email");
	const redirectTo = searchParams.get("redirectTo");

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: email ?? "",
			password: "",
			mode: config.auth.enablePasswordLogin ? "password" : "magic-link",
		},
	});

	const redirectPath = invitationId
		? `/app/organization-invitation/${invitationId}`
		: (redirectTo ?? config.auth.redirectAfterSignIn);

	useEffect(() => {
		if (sessionLoaded && user) {
			router.replace(redirectPath);
		}
	}, [user, sessionLoaded]);

	const onSubmit: SubmitHandler<FormValues> = async (values) => {
		try {
			if (values.mode === "password") {
				const { data, error } = await authClient.signIn.email({
					...values,
				});

				if (error) {
					throw error;
				}

				// The twoFactor plugin answers a password sign-in by a 2FA user
				// with `{ twoFactorRedirect: true }` and *no* session — it
				// deletes the one the credential handler just created. So this
				// is a successful first factor, not a completed sign-in.
				if (data && "twoFactorRedirect" in data) {
					setTwoFactorCode("");
					setUseBackupCode(false);
					setTwoFactorError(null);
					setStep("two-factor");
					return;
				}

				queryClient.invalidateQueries({
					queryKey: sessionQueryKey,
				});

				router.replace(redirectPath);
			} else {
				const { error } = await authClient.signIn.magicLink({
					...values,
					callbackURL: redirectPath,
				});

				if (error) {
					throw error;
				}
			}
		} catch (e) {
			form.setError("root", {
				message: getAuthErrorMessage(
					e && typeof e === "object" && "code" in e
						? (e.code as string)
						: undefined,
				),
			});
		}
	};

	/**
	 * Step one: find out which credential this address actually uses, then
	 * either hand off to the organization's identity provider or fall through to
	 * the password/magic-link step.
	 *
	 * Every failure path lands on the credentials step. The lookup is a routing
	 * hint, not an authorization decision — if it is unreachable, or the IdP
	 * hand-off fails, everyone who is not an SSO user must still be able to sign
	 * in.
	 */
	const continueWithEmail = async () => {
		if (!(await form.trigger("email"))) {
			return;
		}

		const emailValue = form.getValues("email");

		form.clearErrors("root");
		setIsResolvingMode(true);

		let mode: "sso" | "credentials" = "credentials";

		try {
			const response = await apiClient["auth-options"].login.$post({
				json: { email: emailValue },
			});

			if (response.ok) {
				({ mode } = (await response.json()) as {
					mode: "sso" | "credentials";
				});
			}
		} catch {
			// Falls through to the credentials step below.
		}

		if (mode === "sso") {
			setIsRedirectingToSso(true);

			try {
				const { data, error } = await authClient.signIn.sso({
					email: emailValue,
					callbackURL: redirectPath,
				});

				if (error) {
					throw error;
				}

				if (data?.url) {
					// Leave the redirecting state on screen: the browser is
					// navigating away to the identity provider.
					window.location.href = data.url;
					return;
				}

				throw new Error("SSO sign-in returned no authorization URL");
			} catch (e) {
				form.setError("root", {
					message: getAuthErrorMessage(
						e && typeof e === "object" && "code" in e
							? (e.code as string)
							: undefined,
					),
				});
			}
		}

		setIsResolvingMode(false);
		setIsRedirectingToSso(false);
		setStep("credentials");
	};

	// A link that already carries the address — an invitation email, mostly —
	// should not make the recipient press Continue on a prefilled field.
	const autoContinuedRef = useRef(false);
	useEffect(() => {
		if (!email || autoContinuedRef.current) {
			return;
		}

		autoContinuedRef.current = true;
		void continueWithEmail();
	}, [email]);

	const backToEmailStep = () => {
		form.clearErrors();
		form.setValue("password", "");
		setTwoFactorCode("");
		setUseBackupCode(false);
		setTrustDevice(false);
		setTwoFactorError(null);
		setStep("email");
	};

	/**
	 * Second factor. Both endpoints consume the pending-sign-in cookie and set
	 * the real session cookie on success, so the completion path is identical to
	 * a password sign-in that never needed a challenge.
	 *
	 * `trustDevice` is only offered for TOTP: a backup code is the recovery path,
	 * and remembering the device for 30 days off the back of one is the wrong
	 * default when the user has already lost their authenticator.
	 */
	const verifyTwoFactor = async () => {
		setIsVerifyingTwoFactor(true);
		setTwoFactorError(null);

		try {
			const { error } = useBackupCode
				? await authClient.twoFactor.verifyBackupCode({
						code: twoFactorCode,
					})
				: await authClient.twoFactor.verifyTotp({
						code: twoFactorCode,
						trustDevice,
					});

			if (error) {
				throw error;
			}

			queryClient.invalidateQueries({
				queryKey: sessionQueryKey,
			});

			router.replace(redirectPath);
		} catch (e) {
			const code =
				e && typeof e === "object" && "code" in e
					? (e.code as string)
					: undefined;

			setTwoFactorError(
				code === "INVALID_CODE" ||
					code === "INVALID_BACKUP_CODE" ||
					code === undefined
					? t("auth.login.twoFactor.invalidCode")
					: getAuthErrorMessage(code),
			);
		} finally {
			setIsVerifyingTwoFactor(false);
		}
	};

	const signInWithPasskey = async () => {
		try {
			await authClient.signIn.passkey();

			router.replace(redirectPath);
		} catch (e) {
			form.setError("root", {
				message: getAuthErrorMessage(
					e && typeof e === "object" && "code" in e
						? (e.code as string)
						: undefined,
				),
			});
		}
	};

	const signinMode = form.watch("mode");

	return (
		<div>
			<h1 className="font-extrabold text-2xl md:text-3xl text-center">
				{t("auth.login.title")}
			</h1>
			<p className="mt-1 mb-6 text-foreground/60 text-center">
				{t("auth.login.subtitle")}
			</p>

			{form.formState.isSubmitSuccessful &&
			signinMode === "magic-link" ? (
				<Alert variant="success">
					<MailboxIcon className="size-6" />
					<AlertTitle>
						{t("auth.login.hints.linkSent.title")}
					</AlertTitle>
					<AlertDescription>
						{t("auth.login.hints.linkSent.message")}
					</AlertDescription>
				</Alert>
			) : (
				<>
					{invitationId && (
						<OrganizationInvitationAlert className="mb-6" />
					)}

					<Form {...form}>
						<form
							className="space-y-4"
							onSubmit={
								step === "email"
									? (e) => {
											e.preventDefault();
											void continueWithEmail();
										}
									: step === "two-factor"
										? (e) => {
												e.preventDefault();
												void verifyTwoFactor();
											}
										: form.handleSubmit(onSubmit)
							}
						>
							{step === "credentials" &&
								config.auth.enableMagicLink &&
								config.auth.enablePasswordLogin && (
									<LoginModeSwitch
										activeMode={signinMode}
										onChange={(mode) =>
											form.setValue(
												"mode",
												mode as typeof signinMode,
											)
										}
									/>
								)}

							{/* Not gated on isSubmitted: the email step sets root
							    errors (a failed SSO hand-off) outside handleSubmit,
							    where isSubmitted never becomes true. Root errors are
							    only ever set explicitly, so rendering them
							    unconditionally is safe. */}
							{form.formState.errors.root?.message && (
								<Alert variant="error">
									<AlertTriangleIcon className="size-6" />
									<AlertTitle>
										{form.formState.errors.root.message}
									</AlertTitle>
								</Alert>
							)}

							{step === "email" ? (
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t("auth.signup.email")}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													autoComplete="email"
													autoFocus
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							) : (
								<div className="flex items-baseline justify-between gap-4">
									<p className="min-w-0 truncate font-medium text-sm">
										{form.getValues("email")}
									</p>
									<button
										type="button"
										onClick={backToEmailStep}
										className="shrink-0 text-foreground/60 text-xs underline"
									>
										{t("auth.login.notYou")}
									</button>
								</div>
							)}

							{step === "credentials" &&
								config.auth.enablePasswordLogin &&
								signinMode === "password" && (
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex justify-between gap-4">
													<FormLabel>
														{t(
															"auth.signup.password",
														)}
													</FormLabel>

													<Link
														href="/auth/forgot-password"
														className="text-foreground/60 text-xs"
													>
														{t(
															"auth.login.forgotPassword",
														)}
													</Link>
												</div>
												<FormControl>
													<div className="relative">
														<Input
															type={
																showPassword
																	? "text"
																	: "password"
															}
															className="pr-10"
															{...field}
															autoComplete="current-password"
														/>
														<button
															type="button"
															onClick={() =>
																setShowPassword(
																	!showPassword,
																)
															}
															className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary text-xl"
														>
															{showPassword ? (
																<EyeOffIcon className="size-4" />
															) : (
																<EyeIcon className="size-4" />
															)}
														</button>
													</div>
												</FormControl>
											</FormItem>
										)}
									/>
								)}

							{step === "two-factor" && (
								<div className="space-y-4">
									{twoFactorError && (
										<Alert variant="error">
											<AlertTriangleIcon className="size-6" />
											<AlertTitle>
												{twoFactorError}
											</AlertTitle>
										</Alert>
									)}

									<div className="space-y-2">
										<Label htmlFor="two-factor-login-code">
											{useBackupCode
												? t(
														"auth.login.twoFactor.backupCodeLabel",
													)
												: t(
														"auth.login.twoFactor.codeLabel",
													)}
										</Label>
										<Input
											id="two-factor-login-code"
											value={twoFactorCode}
											onChange={(e) =>
												setTwoFactorCode(e.target.value)
											}
											inputMode={
												useBackupCode
													? "text"
													: "numeric"
											}
											autoComplete="one-time-code"
											autoFocus
										/>
									</div>

									{!useBackupCode && (
										<div className="flex items-center gap-2">
											<Checkbox
												id="two-factor-trust-device"
												checked={trustDevice}
												onCheckedChange={(checked) =>
													setTrustDevice(
														checked === true,
													)
												}
											/>
											<Label
												htmlFor="two-factor-trust-device"
												className="font-normal text-sm"
											>
												{t(
													"auth.login.twoFactor.trustDevice",
												)}
											</Label>
										</div>
									)}

									<button
										type="button"
										className="text-foreground/60 text-xs underline"
										onClick={() => {
											setUseBackupCode(!useBackupCode);
											setTwoFactorCode("");
											setTwoFactorError(null);
											// A backup code is the recovery
											// path; don't carry a 30-day trust
											// grant into it.
											setTrustDevice(false);
										}}
									>
										{useBackupCode
											? t(
													"auth.login.twoFactor.useAuthenticator",
												)
											: t(
													"auth.login.twoFactor.useBackupCode",
												)}
									</button>
								</div>
							)}

							<Button
								className="w-full"
								type="submit"
								variant="secondary"
								loading={
									step === "email"
										? isResolvingMode
										: step === "two-factor"
											? isVerifyingTwoFactor
											: form.formState.isSubmitting
								}
								disabled={
									step === "two-factor" && !twoFactorCode
								}
							>
								{step === "email"
									? isRedirectingToSso
										? t("auth.login.ssoRedirecting")
										: t("auth.login.continue")
									: step === "two-factor"
										? t("auth.login.twoFactor.submit")
										: signinMode === "magic-link"
											? t("auth.login.sendMagicLink")
											: t("auth.login.submit")}
							</Button>
						</form>
					</Form>

					{step === "email" &&
						(config.auth.enablePasskeys ||
							(config.auth.enableSignup &&
								config.auth.enableSocialLogin)) && (
							<>
								<div className="relative my-6 h-4">
									<hr className="relative top-2" />
									<p className="-translate-x-1/2 absolute top-0 left-1/2 mx-auto inline-block h-4 bg-card px-2 text-center font-medium text-foreground/60 text-sm leading-tight">
										{t("auth.login.continueWith")}
									</p>
								</div>

								<div className="grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2">
									{config.auth.enableSignup &&
										config.auth.enableSocialLogin &&
										Object.keys(oAuthProviders).map(
											(providerId) => (
												<SocialSigninButton
													key={providerId}
													provider={
														providerId as OAuthProvider
													}
												/>
											),
										)}

									{config.auth.enablePasskeys && (
										<Button
											variant="light"
											className="w-full sm:col-span-2"
											onClick={() => signInWithPasskey()}
										>
											<KeyIcon className="mr-1.5 size-4 text-primary" />
											{t("auth.login.loginWithPasskey")}
										</Button>
									)}
								</div>
							</>
						)}

					{config.auth.enableSignup && (
						<div className="mt-6 text-center text-sm">
							<span className="text-foreground/60">
								{t("auth.login.dontHaveAnAccount")}{" "}
							</span>
							<Link
								href={withQuery(
									"/auth/signup",
									Object.fromEntries(searchParams.entries()),
								)}
							>
								{t("auth.login.createAnAccount")}
								<ArrowRightIcon className="ml-1 inline size-4 align-middle" />
							</Link>
						</div>
					)}
				</>
			)}
		</div>
	);
}
